<template>
  <div class="container d-flex justify-content-center align-items-center" style="min-height: 80vh;">
    <div class="col-md-6">
      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">{{ $t('auth.reset_password') }}</h5>
        </div>
        <div class="card-body">
          <form @submit.prevent="submitNewPassword">
            <div class="mb-3">
              <label for="password" class="form-label">{{ $t('auth.form.password') }}</label>
              <small class="form-text text-muted d-block">
                {{ $t('auth.password_requirements.title') }}
              </small>
              <ul class="list-unstyled password-rules mb-3">
                <li
                  v-for="rule in passwordRequirements"
                  :key="rule.key"
                  :class="['d-flex align-items-center', rule.met ? 'text-success' : 'text-muted']"
                >
                  <i class="fas" :class="rule.met ? 'fa-check-circle' : 'fa-circle'"></i>
                  <span class="ms-2">
                    <template v-if="rule.key === 'special'">
                      {{ $t('auth.password_requirements.special') }} ({{ allowedSpecialChars }})
                    </template>
                    <template v-else>
                      {{ $t(`auth.password_requirements.${rule.key}`) }}
                    </template>
                  </span>
                </li>
              </ul>
              <input
                  type="password"
                  id="password"
                  class="form-control"
                  v-model="password"
                  required
                  :class="{ 'is-invalid': passwordError }"
              />
              <div v-if="passwordError" class="invalid-feedback d-block">
                {{ passwordError }}
              </div>
            </div>

            <div class="mb-3">
              <label for="password_confirmation" class="form-label">{{ $t('auth.form.password_confirmation') }}</label>
              <input
                  type="password"
                  id="password_confirmation"
                  class="form-control"
                  v-model="passwordConfirmation"
                  required
                  :class="{ 'is-invalid': passwordConfirmationError }"
              />
              <div v-if="passwordConfirmationError" class="invalid-feedback d-block">
                {{ passwordConfirmationError }}
              </div>
            </div>

            <div class="text-center">
              <button type="submit" class="btn btn-primary" :disabled="loading">
                <span v-if="!loading">{{ $t('auth.reset_password') }}</span>
                <span v-else>
                  <i class="fa fa-spinner fa-spin"></i> {{ $t('auth.sending') }}
                </span>
              </button>
            </div>
          </form>

          <div v-if="error" class="alert alert-danger mt-3 text-center">
            {{ error }}
          </div>
          <div v-if="success" class="alert alert-success mt-3 text-center">
            {{ $t('auth.password_reset_success') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ForgotPasswordReset",
  data() {
    return {
      password: '',
      passwordConfirmation: '',
      loading: false,
      error: null,
      success: false,
      passwordError: null,
      passwordConfirmationError: null,
      allowedSpecialChars: '@$!%*?&'
    }
  },
  computed: {
    passwordRequirements() {
      const rules = this.getPasswordRulesStatus();
      return [
        { key: 'minLength', met: rules.minLength },
        { key: 'lowercase', met: rules.lowercase },
        { key: 'uppercase', met: rules.uppercase },
        { key: 'number', met: rules.number },
        { key: 'special', met: rules.special },
      ];
    },
  },
  methods: {
    getPasswordRulesStatus() {
      const password = this.password || '';
      return {
        minLength: password.length >= 6,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        number: /\d/.test(password),
        special: /[@$!%*?&]/.test(password),
      };
    },
    validatePasswords() {
      this.passwordError = null;
      this.passwordConfirmationError = null;

      if (!Object.values(this.getPasswordRulesStatus()).every(Boolean)) {
        this.passwordError = this.$t('auth.password_validation_error');
      }

      if (!this.passwordConfirmation) {
        this.passwordConfirmationError = this.$t('auth.password_confirmation_required');
        return;
      }

      if (this.password !== this.passwordConfirmation) {
        this.passwordConfirmationError = this.$t('auth.password_confirmation_mismatch');
      }
    },
    async submitNewPassword() {
      this.validatePasswords();
      if (this.passwordError || this.passwordConfirmationError) {
        return;
      }

      this.loading = true;
      this.error = null;
      this.success = false;

      try {
        const token = this.$route.params.token;
        const email = this.$route.query.email;

        if (!token || !email) {
          this.error = this.$t('auth.reset_link_invalid');
          return;
        }

        const response = await this.$axios.post('/reset-password', {
          token,
          email,
          password: this.password,
          password_confirmation: this.passwordConfirmation
        });

        if (response.status === 200 && response.data?.token && response.data?.user) {
          await this.$store.dispatch('user/setLogin', response.data);
          this.success = true;
          this.password = '';
          this.passwordConfirmation = '';
          await this.$router.replace('/home');
        }
      } catch (err) {
        console.error(err);
        this.error = err.response?.data?.message || this.$t('auth.error_occurred');
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style scoped>
.password-rules {
  font-size: 0.875rem;
}

.password-rules .fa-circle,
.password-rules .fa-check-circle {
  font-size: 0.75rem;
}
</style>
